import Cookies from "js-cookie";
import { CheckoutPlan, decorateUrlWithUTMParams, Web46DiscountPercent } from "../LandingPage";
import { getInternalCoreEndpoint } from "../utils";
import { sha256 } from "js-sha256";
import { User } from "../types";
import { LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY } from "./useAnswerTracker";
import { Web29AnchorCheckoutMode } from "../web29Offer";
import { isWelcomeKitPlan, WELCOME_KIT_OFFER_ID } from "../features/welcomeKit/welcomeKit";
import { getProteinTargetG } from "../quiz/protein";
import { getCheckoutReferralCode } from "../referral";
import { getStoredShareId } from "../shareLink";
import i18n from "../i18n";

export type Web46DiscountArm = "personalization" | "discount" | "discount30";
// Black Friday 2026 (bfSale.ts): the sale phase's coupon pair. Display name
// BLACKFRIDAY30 / BLACKFRIDAY40 on Stripe checkout and receipts.
export type BfSaleDiscountKey = "bf2026_early" | "bf2026_bfcm";
// Every coupon offer that rides Core's create-discount route on control's
// price ids (single and Gym Buddy plans alike).
export type CheckoutDiscountKey = Web46DiscountArm | BfSaleDiscountKey;

const WEB46_COUPONS: Record<CheckoutDiscountKey, { annual: string; monthly: string }> = {
  personalization: {
    annual: "WEB46_MATCH40_ANNUAL",
    monthly: "WEB46_MATCH40_MONTHLY",
  },
  discount: {
    annual: "WEB46_SPECIAL40_ANNUAL",
    monthly: "WEB46_SPECIAL40_MONTHLY",
  },
  // 12.5% off control's price = 30% off the anchor.
  discount30: {
    annual: "WEB46_SPECIAL30_ANNUAL",
    monthly: "WEB46_SPECIAL30_MONTHLY",
  },
  // Early Black Friday: 12.5% off control's price = 30% off the anchor, first
  // year (annual, once) / first 12 months (monthly, repeating).
  bf2026_early: {
    annual: "BF2026_NEWUSER_EARLY_ANNUAL",
    monthly: "BF2026_NEWUSER_EARLY_MONTHLY",
  },
  // BFCM (follow-up phase): 25% off control's price = 40% off the anchor.
  bf2026_bfcm: {
    annual: "BF2026_NEWUSER_ANNUAL",
    monthly: "BF2026_NEWUSER_MONTHLY",
  },
};

export const getWeb46DiscountPercent = (arm: CheckoutDiscountKey): Web46DiscountPercent =>
  arm === "discount30" || arm === "bf2026_early" ? 30 : 40;

// Control's list price ($189/yr, $29.99/mo) is itself 20% off the anchor the
// funnel advertises against; the after-trial line names it on the renewal so
// the first-year percent is not read as a discount from the renewal price.
export const CONTROL_PERCENT_OFF_ANCHOR = 20;

export function getWeb46CouponId(
  plan: CheckoutPlan | undefined,
  arm: CheckoutDiscountKey | undefined
): string | undefined {
  if (!arm || (plan?.id !== "committed" && plan?.id !== "serious")) return undefined;
  return plan.id === "committed"
    ? WEB46_COUPONS[arm].annual
    : WEB46_COUPONS[arm].monthly;
}

// Shared by initial checkout and promo-code session recreation. These are
// independent metadata fields: a welcome kit must not replace the trial mode.
export function getCheckoutOfferMetadata(
  plan: CheckoutPlan | undefined,
  isNoTrial: boolean,
  urgencyAnchorMode: Web29AnchorCheckoutMode | null,
  // web40 variation only: the annual welcome kit is part of this checkout.
  welcomeKit = false
): Record<string, string> {
  const metadata: Record<string, string> = {};
  if (isNoTrial) {
    metadata.pricing_variant = 'no_trial';
  } else if (urgencyAnchorMode) {
    metadata.pricing_variant = urgencyAnchorMode === 'offer'
      ? 'urgency_anchor'
      : `urgency_anchor_${urgencyAnchorMode}`;
  }
  if (welcomeKit && isWelcomeKitPlan(plan)) {
    metadata.offer_id = WELCOME_KIT_OFFER_ID;
  }
  return metadata;
}

export function createGetClientSecretPromise(
  plan?: CheckoutPlan,
  eventID?: string,
  user?: User,
  isWeb17Variation?: boolean,
  // No trial (web35 variation, or ?trial=false): pay today on the plan's own
  // price, no promotion code — the 20% is a display anchor. useCheckoutPlan
  // has already picked control's price (offer live/revived) or the regular
  // ÷0.8 price (expired) via urgencyAnchorMode.
  isNoTrial = false,
  // Site baseline offer state: "offer" / "email" ride control's price,
  // "expired" the regular (÷0.8) price — no coupon in any mode; the value only
  // sets the Stripe pricing_variant tag.
  urgencyAnchorMode: Web29AnchorCheckoutMode | null = null,
  // web40_welcome_kit variation: tag the session with the kit offer.
  welcomeKit = false,
  // web46_match_offer: 25% off control's price produces the advertised 40%
  // anchor discount (12.5% → 30% for discount30). The caller only supplies an
  // arm while the timed offer is
  // active and trial checkout is allowed.
  web46DiscountArm?: CheckoutDiscountKey
) {
  const mode = "web";
  const subscriptionPriceId = plan?.priceId ?? "";
  const unlockProductId = plan?.unlockProductId ?? "";
  const trialProductId = plan?.trialProductId ?? "";

  const amplitudeId = Cookies.get("bws_amplitude_device_id");
  const sessionIdentitySessionId = localStorage.getItem(
      LOCAL_STORAGE_SESSION_IDENTITY_SESSION_ID_KEY
  );

  let urlObj: URL;
  const web17_3DiscountId = plan?.experiments?.web17_3?.discount;
  const web46DiscountId = getWeb46CouponId(plan, web46DiscountArm);
  if (isNoTrial) {
    // create-subscription (no trial product → charged today) on the plan's
    // price with NO promotion code. Tagged pricing_variant=no_trial: the
    // backend's HubSpot workflow fork and the Meta "MetaStartSubscription"
    // event key on that exact value; offer metadata preserves that tag.
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-subscription/${subscriptionPriceId}/${unlockProductId}`
    );
  } else if (isWeb17Variation && web17_3DiscountId) {
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-discount/${subscriptionPriceId}/${unlockProductId}/${trialProductId}/${web17_3DiscountId}`
    );
  } else if (web46DiscountId) {
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-discount/${subscriptionPriceId}/${unlockProductId}/${trialProductId}/${web46DiscountId}`
    );
  } else {
    urlObj = new URL(
      `${getInternalCoreEndpoint()}/internal/public/checkout/${mode}/create-trial/${subscriptionPriceId}/${unlockProductId}/${trialProductId}`
    );
  }

  // Site baseline offer tag: the backend stamps Stripe metadata pricingVariant
  // on the session + subscription (checkout.controller.ts METADATA_MAPPING):
  // urgency_anchor (offer redeemed on the pricing page), urgency_anchor_email
  // (revived via email), urgency_anchor_expired (paid the regular price). Pure
  // metadata. Core must validate annual eligibility and persist offer_id on
  // the session + subscription before HubSpot can consume it. A browser signal
  // never grants fulfillment eligibility.
  for (const [key, value] of Object.entries(getCheckoutOfferMetadata(plan, isNoTrial, urgencyAnchorMode, welcomeKit))) {
    urlObj.searchParams.set(key, value);
  }

  // web42: the protein target the quiz showed; Core stores it on the session
  // (metadata proteinTargetG) for the post-purchase protein offer.
  const proteinTargetG = getProteinTargetG(user);
  if (proteinTargetG) urlObj.searchParams.set("protein_target_g", String(proteinTargetG));

  // Spanish quiz: core sends Spanish buyers to Spanish post-purchase pages and
  // marks the Stripe customer's language. English requests are unchanged.
  if (i18n.language === "es") urlObj.searchParams.set("lang", "es");
  urlObj.searchParams.set("ui_mode", "custom");
  urlObj.searchParams.set("tos_shown", "false");

  const emailToUse = (user?.email || "").trim();
  if (emailToUse) {
    urlObj.searchParams.set("email", sha256(emailToUse)); // Hashed for tracking
    urlObj.searchParams.set("prefillEmail", emailToUse); // Actual email for customer_email
  }

  if (user?.name) {
    urlObj.searchParams.set("name", user.name);
  }

  if (amplitudeId) {
    urlObj.searchParams.set("amplitudeId", amplitudeId);
  }

  if (eventID) {
      urlObj.searchParams.set("fb_event_id", eventID);
  }

  if (sessionIdentitySessionId) {
      urlObj.searchParams.set(
          "session_identity_session_id",
          sessionIdentitySessionId
      );
  }

  // Attribution: utm/click ids, plus kelly_funnel_cell and stable_id for
  // kelly_funnel visitors (services/checkoutAttribution.ts).
  decorateUrlWithUTMParams(urlObj);

  return async () => {
    const referralCode = getCheckoutReferralCode(plan, !isNoTrial && !(isWeb17Variation && web17_3DiscountId));
    if (referralCode) urlObj.searchParams.set("referral_code", referralCode);
    else urlObj.searchParams.delete("referral_code");
    // Attribution, not eligibility: every checkout route carries the share id.
    const shareId = getStoredShareId();
    if (shareId) urlObj.searchParams.set("share_id", shareId);
    else urlObj.searchParams.delete("share_id");
    const res = await fetch(urlObj.toString(), {
      method: "GET",
    });
    const data = await res.json();
    return data.client_secret;
  };
}
